import { takeUntil, filter } from 'rxjs/operators';
import type { History } from '@o2d/history';

import type { WhiteboardBus } from '../../core/bus/whiteboard-bus.js';
import type { UiAction } from '../../core/bus/ui-action.events.js';
import { objectId } from '../../core/bus/branded-types.js';
import type { BoardState } from '../../core/state/board-state.js';
import { AddObjectCommand } from '../../core/command/add-object.command.js';
import { createEllipseDefaults, type CreateEllipseDefaultsOptions } from './ellipse-defaults.factory.js';

/**
 * Subscribes to `place/ellipse` UiActions and materialises a new ellipse:
 * builds the EllipseObject via `createEllipseDefaults`, pushes an
 * `AddObjectCommand` onto history, then re-emits `selection/set` for the
 * new id (TR6).
 *
 * Per PRD-F1 T22: ellipses have no inline-edit lifecycle, so this controller
 * deliberately does NOT dispatch `edit/enter-ellipse`.
 *
 * Lifecycle: subscription is bound to `bus.destroy$` via `takeUntil`. The
 * controller is NOT a Lit ReactiveController — its lifetime equals the bus.
 *
 * FC4: never publishes onto `selection$`/`tool$`/`viewport$` directly;
 * downstream adapters translate `selection/set` into a `selection$` write.
 */
export class EllipsePlacementController {
    readonly #bus: WhiteboardBus;
    readonly #state: BoardState;
    readonly #history: History<BoardState>;

    constructor(bus: WhiteboardBus, state: BoardState, history: History<BoardState>) {
        this.#bus = bus;
        this.#state = state;
        this.#history = history;

        this.#bus.uiActions$
            .pipe(
                filter(
                    (a): a is Extract<UiAction, { type: 'place/ellipse' }> =>
                        a.type === 'place/ellipse',
                ),
                takeUntil(this.#bus.destroy$),
            )
            .subscribe({
                next: (action) => this.#handlePlace(action),
                error: () => {
                    if (import.meta.env?.DEV) {
                        console.warn('[EllipsePlacementController] bus error swallowed');
                    }
                },
            });
    }

    #handlePlace(action: Extract<UiAction, { type: 'place/ellipse' }>): void {
        const scale = this.#bus.viewport$.value.scale;
        // Stefan, 2026-09-03 — "choose properties before drawing", see the
        // identical comment in rectangle-placement.controller.ts.
        const staged = this.#bus.stagedToolProperties$.value.ellipse;
        // Bugreport (Anke, 2026-09-03): "fehlen für: schatten, transparenz",
        // see the identical comment in rectangle-placement.controller.ts.
        const overrides: CreateEllipseDefaultsOptions | undefined = staged && {
            strokeColor: staged['strokeColor'] as string | undefined,
            strokeWidth: staged['strokeWidth'] as number | undefined,
            fill: staged['fill'] as string | undefined,
            boxShadow: staged['boxShadow'] as 'on' | undefined,
            backgroundOpacity: staged['backgroundOpacity'] as number | undefined,
            strokeStyle: staged['strokeStyle'] as 'solid' | 'dashed' | 'dotted' | undefined,
        };
        const ellipse = createEllipseDefaults(
            scale,
            { x: action.rect.x, y: action.rect.y },
            { width: action.rect.width, height: action.rect.height },
            overrides,
        );

        const cmd = new AddObjectCommand(ellipse);
        this.#history.push(cmd, this.#state);

        const newId = objectId(ellipse.id);
        this.#bus.uiActions$.next({ type: 'selection/set', ids: [newId] });
    }
}
